import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Users, Phone, TrendingUp, Target } from "lucide-react";
import { getFullName } from "../../services/common/util";
import { getDeskDetailsById } from "../../services/crmServices/deskService";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export function DeskDetailModal({ open, onClose, desk }) {
    const [deskDetails, setDeskDetails] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (!desk) return;
        getDeskDetailsById(desk._id)
            .then((response) => {
                if (response.status === 200) {
                    setDeskDetails(response.data);
                }
                // Handle the response if needed
            })
            .catch((error) => {
                console.error("Error fetching desk details:", error);
            });
    }, [desk]);

    const handleAgentClick = (agentId) => {
        if (!agentId) {
            console.error("Agent ID is undefined, cannot navigate");
            return;
        }
        navigate(`/agent/${agentId}`);
        onClose(); // Close the modal when navigating to agent page
    };

    if (!deskDetails) return null;

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader className="border-b pb-4">
                    <DialogTitle className="text-2xl font-bold text-gray-900 flex items-center">
                        <Users className="h-6 w-6 mr-3 text-blue-600" />
                        {deskDetails.name}
                    </DialogTitle>
                    <DialogDescription className="text-gray-600">
                        Comprehensive overview of desk performance and team members
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-5 pt-2">
                    {/* Desk Management */}
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 shadow-sm">
                        <h3 className="text-sm font-semibold text-gray-900 flex items-center mb-4">
                            <svg className="w-4 h-4 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                            Management Team
                        </h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                                <div>
                                    <p className="text-xs text-gray-600 font-medium mb-1">Floor Manager</p>
                                    {deskDetails.floorManager ? (
                                        <p className="font-semibold text-gray-900">{getFullName(deskDetails.floorManager)}</p>
                                    ) : (
                                        <p className="text-gray-400 italic">Not assigned</p>
                                    )}
                                </div>
                                <Badge className="bg-blue-600 text-white">Floor</Badge>
                            </div>
                            <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                                <div>
                                    <p className="text-xs text-gray-600 font-medium mb-1">Desk Manager</p>
                                    {deskDetails.manager ? (
                                        <p className="font-semibold text-gray-900">{getFullName(deskDetails.manager)}</p>
                                    ) : (
                                        <p className="text-gray-400 italic">Not assigned</p>
                                    )}
                                </div>
                                <Badge className="bg-indigo-600 text-white">Desk</Badge>
                            </div>
                        </div>
                    </div>

                    {/* Performance Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="bg-gray-50 rounded-lg p-5 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs text-gray-500 font-medium mb-1">Total Leads</p>
                                    <p className="text-3xl font-bold text-blue-600">
                                        {deskDetails.leadCount}
                                    </p>
                                </div>
                                <div className="p-3 bg-blue-100 rounded-full">
                                    <Target className="h-6 w-6 text-blue-600" />
                                </div>
                            </div>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-5 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs text-gray-500 font-medium mb-1">Calls Today</p>
                                    <p className="text-3xl font-bold text-green-600">
                                        {deskDetails.callTodayCount}
                                    </p>
                                </div>
                                <div className="p-3 bg-green-100 rounded-full">
                                    <Phone className="h-6 w-6 text-green-600" />
                                </div>
                            </div>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-5 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs text-gray-500 font-medium mb-1">Conversion</p>
                                    <p className="text-3xl font-bold text-purple-600">
                                        {deskDetails.conversionRate}%
                                    </p>
                                </div>
                                <div className="p-3 bg-purple-100 rounded-full">
                                    <TrendingUp className="h-6 w-6 text-purple-600" />
                                </div>
                            </div>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-5 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs text-gray-500 font-medium mb-1">Team Size</p>
                                    <p className="text-3xl font-bold text-orange-600">
                                        {deskDetails.teamSize}
                                    </p>
                                </div>
                                <div className="p-3 bg-orange-100 rounded-full">
                                    <Users className="h-6 w-6 text-orange-600" />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Team Members */}
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 shadow-sm">
                        <h3 className="text-sm font-semibold text-gray-900 flex items-center mb-4">
                            <svg className="w-4 h-4 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            Team Members ({deskDetails.agents.length})
                        </h3>
                        <div className="space-y-2">
                            {deskDetails.agents.map((member) => (
                                <div
                                    key={member._id || member}
                                    className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 hover:border-blue-400:border-blue-500 hover:shadow-md transition-all"
                                >
                                    <div className="flex items-center space-x-3">
                                        <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center shadow-sm">
                                            <span className="text-sm font-bold text-white">
                                                {`${member.firstName?.charAt(0) || ""}${
                                                    member.lastName ? member.lastName.charAt(0) : ""
                                                }`}
                                            </span>
                                        </div>
                                        <div>
                                            <Button
                                                variant="link"
                                                className="p-0 h-auto text-sm font-semibold text-gray-900 hover:text-blue-600:text-blue-400"
                                                onClick={() => handleAgentClick(member._id)}
                                                disabled={!member._id}
                                            >
                                                {getFullName(member)}
                                            </Button>
                                            {member.userName && (
                                                <p className="text-xs text-blue-600">
                                                    @{member.userName}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-6">
                                        <div className="text-right">
                                            <p className="text-sm font-semibold text-gray-900">
                                                {member.callCount || 0} <span className="text-xs text-gray-600 font-normal">calls</span>
                                            </p>
                                            <p className="text-xs text-green-600 font-medium">
                                                {member.conversionCount || 0} conversions
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
